import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const [editing, setEditing] = useState(null);
  const [target, setTarget] = useState("");

  const load = () => api.get("/agents").then(r => setAgents(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (a) => { setEditing(a.id); setTarget(String(a.sales_target ?? 0)); };
  const saveEdit = async (id) => {
    try {
      await api.patch(`/users/${id}`, { sales_target: Number(target) || 0 });
      toast.success("Target updated");
      setEditing(null);
      load();
    } catch { toast.error("Failed to update target"); }
  };

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Agents & Territories</h1>
        <p className="text-slate-600 mt-1">Field sales agents, assigned states and monthly sales targets</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map(a => (
          <div key={a.id} className="bg-white rounded-xl border border-slate-200 p-5 card-hoverable" data-testid={`agent-card-${a.id}`}>
            <Link to={`/agents/${a.id}`} className="block">
              <div className="w-12 h-12 rounded-full bg-[#001F54] text-white grid place-items-center font-bold text-lg">{a.name?.[0]}</div>
              <div className="mt-3 text-lg font-semibold">{a.name}</div>
              <div className="text-xs text-slate-500">{a.email}</div>
              <div className="mt-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">Territories</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {(a.assigned_states || []).map(s => <span key={s} className="text-[10px] px-2 py-0.5 bg-slate-100 rounded">{s}</span>)}
                {(!a.assigned_states || a.assigned_states.length === 0) && <span className="text-xs text-slate-400">—</span>}
              </div>
            </Link>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Monthly Sales Target</div>
              {editing === a.id ? (
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="number"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    className="flex-1 h-10 px-2 border border-slate-300 rounded-lg text-sm"
                    data-testid={`target-input-${a.id}`}
                    autoFocus
                  />
                  <button onClick={() => saveEdit(a.id)} className="h-10 w-10 bg-[#003DA5] text-white rounded-lg grid place-items-center" data-testid={`target-save-${a.id}`}><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditing(null)} className="h-10 w-10 border border-slate-300 rounded-lg grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="flex items-center justify-between mt-1">
                  <div className="text-2xl font-heading font-bold text-[#003DA5]">{money(a.sales_target || 0)}</div>
                  {canEdit && (
                    <button onClick={() => startEdit(a)} className="text-slate-400 hover:text-[#003DA5] p-1" data-testid={`target-edit-${a.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
